import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '../../../src/lib/utils';
import Loader from './loader';

const buttonVariants = cva(
    'inline-flex items-center w-fit justify-center whitespace-nowrap rounded-lg text-md font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                destructive: 'bg-destructive/70 text-destructive-foreground hover:bg-destructive',
                secondary: 'bg-gray-100 text-secondary-foreground hover:bg-gray-200',
                ghost: 'hover:text-gray-500 hover:bg-gray-50 border-none',
                link: 'text-emerald-600 hover:text-emerald-700 underline',
                primary: 'bg-emerald-600 text-white hover:bg-emerald-700',
                primaryProject: 'bg-indigo-600 text-white hover:bg-indigo-700',
                outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
                elegible: 'bg-emerald-500 text-white hover:bg-emerald-600',
            },
            size: {
                default: 'h-10 px-4 py-2',
                xs: 'h-7 w-7 px-2',
                sm: 'h-9 px-3 text-sm',
                lg: 'h-11 px-8 text-lg',
                xl: 'h-14 px-8 text-2xl',
                icon: 'h-10 w-10',
                iconXs: 'h-7 w-7',
                iconSm: 'h-9 w-9',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'default',
        },
    },
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    loading?: boolean;
    dataId: string;
    disabled?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({className, variant, size, loading, disabled, dataId, asChild = false, ...props}, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                data-id={dataId}
                className={cn(
                    buttonVariants({variant, size, className}),
                    loading && 'bg-gray-300 text-gray-500 hover:bg-gray-300 cursor-not-allowed',
                    disabled && 'cursor-not-allowed bg-gray-300 text-gray-500 hover:bg-gray-300 opacity-60',
                )}
                disabled={disabled || loading}
                ref={ref}
                {...props}
            >
                {loading ? (
                    <>
            <span className="visually-hidden flex  px-8 gap-4 items-center">
              {'Chargement...'} <Loader size="small" color="primary"/>
            </span>
                    </>
                ) : (
                    props.children
                )}
            </Comp>
        );
    },
);
Button.displayName = 'Button';

export {Button, buttonVariants};
